import React from "react"

export default function AgeRangeSlider({ minAge = 18, maxAge = 80, onRangeChange }) {
  const [minValue, setMinValue] = React.useState(minAge)
  const [maxValue, setMaxValue] = React.useState(maxAge)
  const [isDragging, setIsDragging] = React.useState(null)
  const sliderRef = React.useRef(null)

  const updateValueFromPosition = (clientX, draggingType) => {
    if (!sliderRef.current) return

    const rect = sliderRef.current.getBoundingClientRect()
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const value = Math.round(minAge + percentage * (maxAge - minAge))

    if (draggingType === "min") {
      const newMinValue = Math.max(minAge, Math.min(value, maxValue - 1))
      setMinValue(newMinValue)
      if (onRangeChange) onRangeChange(newMinValue, maxValue)
    } else if (draggingType === "max") {
      const newMaxValue = Math.max(minValue + 1, Math.min(value, maxAge))
      setMaxValue(newMaxValue)
      if (onRangeChange) onRangeChange(minValue, newMaxValue)
    }
  }

  const handleMouseDownOnHandle = (type) => (e) => {
    e.preventDefault()
    setIsDragging(type)
  }

  // New handler for clicking on track itself
  const handleMouseDownOnTrack = (e) => {
    e.preventDefault()
    if (!sliderRef.current) return

    const rect = sliderRef.current.getBoundingClientRect()
    const clickPercentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const clickValue = Math.round(minAge + clickPercentage * (maxAge - minAge))

    // Decide which handle is closer to clickValue
    const distToMin = Math.abs(clickValue - minValue)
    const distToMax = Math.abs(clickValue - maxValue)
    const draggingType = distToMin <= distToMax ? "min" : "max"

    // Update handle value immediately
    updateValueFromPosition(e.clientX, draggingType)
    // Start dragging that handle
    setIsDragging(draggingType)
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    updateValueFromPosition(e.clientX, isDragging)
  }

  const handleMouseUp = () => {
    setIsDragging(null)
  }

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }
    }
  }, [isDragging])

  const minPercentage = ((minValue - minAge) / (maxAge - minAge)) * 100
  const maxPercentage = ((maxValue - minAge) / (maxAge - minAge)) * 100

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Age Band</label>
        <span className="text-lg font-semibold text-[#7B189F]">
          {minValue}-{maxValue} years
        </span>
      </div>

      <div className="relative py-4">
        {/* Slider track */}
        <div
          ref={sliderRef}
          className="relative h-2 bg-gray-200 rounded-full cursor-pointer"
          onMouseDown={handleMouseDownOnTrack} // <-- Add handler here
        >
          {/* Active range */}
          <div
            className="absolute h-2 bg-[#7B189F] rounded-full"
            style={{
              left: `${minPercentage}%`,
              width: `${maxPercentage - minPercentage}%`,
            }}
          />

          {/* Min handle */}
          <div
            className="absolute w-6 h-6 bg-[#7B189F] rounded-full cursor-grab active:cursor-grabbing transform -translate-y-2 -translate-x-3 shadow-lg hover:scale-110 transition-transform"
            style={{ left: `${minPercentage}%` }}
            onMouseDown={handleMouseDownOnHandle("min")}
          />

          {/* Max handle */}
          <div
            className="absolute w-6 h-6 bg-[#7B189F] rounded-full cursor-grab active:cursor-grabbing transform -translate-y-2 -translate-x-3 shadow-lg hover:scale-110 transition-transform"
            style={{ left: `${maxPercentage}%` }}
            onMouseDown={handleMouseDownOnHandle("max")}
          />
        </div>

        {/* Labels */}
        <div className="flex justify-between mt-2 text-sm text-gray-600">
          <span>{minAge} years</span>
          <span>{maxAge} years</span>
        </div>
      </div>
    </div>
  )
}

